<template lang="pug">
div
  //- единая правая часть Telegram-чата
  //- Используется в клиентах, лидах и на странице unidentified-чата
  template(v-if="chat")
    ds-panel(class="tg-chat-current-card")
      ds-panel-element(offset="bottom")
        ds-caption(variant="strong") {{ title }}
      ds-panel-element(offset="bottom")
        ds-caption(size="sm") {{ subtitle }}

    loading-wrapper(:loading="messagesLoading" size="2xl")
      template(v-if="displayedMessageList.length")
        .tg-chat-messages-scroll(
          ref="messagesScroll"
          @scroll.passive="handleMessagesScroll"
        )
          .tg-chat-messages-pagination(v-if="messageList.length")
            control-button(
              v-if="hasMoreOlderMessages"
              styling="default"
              :disabled="isLoadingOlderMessages"
              @click.prevent="loadOlderMessages"
            ) {{ isLoadingOlderMessages ? _('Loading...') : _('Load older messages') }}

            ds-caption(
              v-else
              size="sm"
            ) {{ _('No older messages') }}

          .tg-chat-messages
            .tg-chat-message(
              v-for="message in displayedMessageList"
              :key="getMessageKey(message)"
              :class="getMessageClasses(message)"
            )
              .tg-chat-message__bubble
                ds-caption(
                  v-if="message.text"
                  class="tg-chat-message__text"
                ) {{ message.text }}

                ds-caption(
                  v-if="message.isOutgoingPlaceholder && message.outgoingStatus === 'failed' && message.error"
                  size="sm"
                  class="tg-chat-message__error"
                ) {{ message.error }}

                ds-link(
                  v-if="message.file && message.file.url"
                  class="tg-chat-message__file"
                  :href="message.file.url"
                  target="_blank"
                ) {{ message.file.name || _('Open file') }}

                ds-caption(
                  size="sm"
                  class="tg-chat-message__meta"
                ) {{ getMessageMeta(message) }}

                ds-caption(
                  v-if="getMessageReadStatus(message)"
                  size="sm"
                  class="tg-chat-message__read-meta"
                ) {{ getMessageReadStatus(message) }}

      template(v-else)
        .tg-chat-messages-scroll(
          ref="messagesScroll"
          @scroll.passive="handleMessagesScroll"
        )
          ds-panel: ds-panel-element(offset="vertical")
            ds-caption(size="sm") {{ resolvedMessagesEmptyStateMessage }}

    telegram-message-composer(
      :message-form="messageForm"
      :is-sending-message="isSendingMessage"
      :can-send-message="canSendMessage"
      :is-refresh-disabled="isRefreshDisabled || messagesLoading"
      @send="submitMessage"
      @refresh="manualRefresh"
    )

  template(v-else)
    ds-panel: ds-panel-element(offset="vertical")
      ds-caption(size="sm") {{ resolvedEmptyStateMessage }}
</template>

<script>
import { tgChatApi } from '@md/tg-chat/api'
import TelegramMessageComposer from './TelegramMessageComposer'

export default {
  name: 'TelegramChatConversation',

  components: {
    TelegramMessageComposer,
  },

  props: {
    chat: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    canSendMessage: {
      type: Boolean,
      default: false,
    },
    isRefreshDisabled: {
      type: Boolean,
      default: false,
    },
    emptyStateMessage: {
      type: String,
      default: '',
    },
    messagesEmptyStateMessage: {
      type: String,
      default: '',
    },
    unreadCount: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      messagesLoading: false,
      isSendingMessage: false,
      messageList: [],
      messageForm: {
        text: '',
      },

      // локальные исходящие сообщения, которые уже поставлены в очередь,
      // но ещё не появились как обычные TelegramMessage
      pendingOutgoingMessages: [],
      // таймеры polling по outgoing id
      outgoingStatusTimers: {},
      // пагинация истории сообщений.
      messagesLimit: 50,
      olderMessagesOffset: 0,
      hasMoreOlderMessages: true,
      isLoadingOlderMessages: false,
      // polling новых сообщений активного чата.
      messagesPollTimer: null,
      messagesPollIntervalMs: 5000,
      outgoingPollIntervalMs: 2000,
      // защита от повторного emit('seen') для одного и того же чата
      seenEmittedChatId: null,
    }
  },

  computed: {
    resolvedEmptyStateMessage() {
      return this.emptyStateMessage || this._('Select a chat to view messages.')
    },

    resolvedMessagesEmptyStateMessage() {
      return this.messagesEmptyStateMessage || this._('There are no messages in this chat yet.')
    },

    displayedMessageList() {
      const activeChatId = this.chat && this.chat.id

      if (!activeChatId) {
        return []
      }

      const pendingItems = this.pendingOutgoingMessages.filter(
        message => message.chatId === activeChatId
      )

      return [...this.messageList, ...pendingItems].sort((left, right) => {
        const leftValue = left.sortAt || left.sentAt || left.createdAt || null
        const rightValue = right.sortAt || right.sentAt || right.createdAt || null

        const leftTime = leftValue ? new Date(leftValue).getTime() : 0
        const rightTime = rightValue ? new Date(rightValue).getTime() : 0

        return leftTime - rightTime
      })
    },
  },

  watch: {
    // при смене чата компонент сам загружает историю и перезапускает polling
    'chat.id': {
      immediate: true,
      async handler(newId, oldId) {
        if (newId !== oldId) {
          await this.bootstrap()
        }
      },
    },
  },

  beforeDestroy() {
    this.stopPolling()
    this.stopAllOutgoingStatusPolling()
  },

  methods: {
    extractListItems(response) {
      if (Array.isArray(response)) {
        return response
      }

      if (Array.isArray(response?.data?.items)) {
        return response.data.items
      }

      if (Array.isArray(response?.items)) {
        return response.items
      }

      if (Array.isArray(response?.results)) {
        return response.results
      }

      return []
    },

    extractPagination(response) {
      // TODO оставить фактический вариант
      return (
        response?.data?.pagination
        || response?.pagination
        || response?.items?.pagination
        || response?.item?.pagination
        || null
      )
    },

    extractSingleItem(response) {
      return response?.item || response?.data?.item || response?.data || response || null
    },

    getMessageSortValue(message) {
      return (
        message?.sentAt
        || message?.createdAt
        || message?.sortAt
        || null
      )
    },

    sortMessagesByDate(messages) {
      return [...messages].sort((left, right) => {
        const leftValue = this.getMessageSortValue(left)
        const rightValue = this.getMessageSortValue(right)

        const leftTime = leftValue ? new Date(leftValue).getTime() : 0
        const rightTime = rightValue ? new Date(rightValue).getTime() : 0

        if (leftTime !== rightTime) {
          return leftTime - rightTime
        }

        return (left.id || 0) - (right.id || 0)
      })
    },

    mergeMessages(existingMessages, incomingMessages) {
      const byId = {}

      existingMessages.forEach(message => {
        byId[message.id] = message
      })

      incomingMessages.forEach(message => {
        byId[message.id] = message
      })

      return this.sortMessagesByDate(Object.values(byId))
    },

    updateOlderMessagesPagination(rawItems, pagination, requestedOffset = 0) {
      const receivedCount = rawItems.length
      const limit = Number(pagination?.limit || this.messagesLimit)
      const offset = Number(pagination?.offset || requestedOffset || 0)
      const total = Number(pagination?.total || pagination?.count || 0)

      this.olderMessagesOffset = offset + receivedCount

      if (total) {
        this.hasMoreOlderMessages = this.olderMessagesOffset < total
        return
      }

      this.hasMoreOlderMessages = receivedCount >= limit
    },

    // начальная загрузка сообщений активного чата.
    async bootstrap() {
      this.stopPolling()
      this.stopAllOutgoingStatusPolling()

      this.messageList = []
      this.pendingOutgoingMessages = []
      this.olderMessagesOffset = 0
      this.hasMoreOlderMessages = true
      this.seenEmittedChatId = null

      if (!(this.chat && this.chat.id)) {
        return
      }

      await this.receiveMessages(this.chat.id, {
        resetPagination: true,
      })

      this.restartMessagesPolling()
    },

    async receiveMessages(
      chatId,
      {
        sinceId = null,
        append = false,
        resetPagination = false,
      } = {}
    ) {
      if (!chatId) {
        this.messageList = []
        this.olderMessagesOffset = 0
        this.hasMoreOlderMessages = true
        return
      }

      if (!append) {
        this.messagesLoading = true
      }

      const shouldStickToBottom = append
        ? this.isMessagesNearBottom()
        : true

      try {
        const response = await tgChatApi.messages.list.execute({
          chatId,
          limit: this.messagesLimit,
          offset: 0,
          since_id: sinceId,
        })

        const rawItems = this.extractListItems(response)
        const pagination = this.extractPagination(response)
        const normalizedItems = rawItems.slice().reverse()

        if (!append) {
          this.messageList = this.sortMessagesByDate(normalizedItems)

          if (resetPagination) {
            this.updateOlderMessagesPagination(rawItems, pagination, 0)
            this.emitSeenIfNeeded()
          }

          this.$nextTick(() => {
            this.scrollMessagesToBottom()
          })

          return
        }

        if (!normalizedItems.length) {
          return
        }

        const knownIds = new Set(this.messageList.map(message => message.id))
        const newItems = normalizedItems.filter(
          message => !knownIds.has(message.id)
        )

        if (!newItems.length) {
          return
        }

        this.messageList = this.mergeMessages(this.messageList, newItems)

        this.$nextTick(() => {
          if (shouldStickToBottom) {
            this.scrollMessagesToBottom()
          }
        })
      } catch (error) {
        if (!append) {
          this.messageList = []
          this.olderMessagesOffset = 0
          this.hasMoreOlderMessages = true
        }
      } finally {
        if (!append) {
          this.messagesLoading = false
        }
      }
    },

    async loadOlderMessages() {
      if (
        !(this.chat && this.chat.id)
        || this.isLoadingOlderMessages
        || !this.hasMoreOlderMessages
      ) {
        return
      }

      const scrollElement = this.getMessagesScrollElement()
      const previousScrollHeight = scrollElement
        ? scrollElement.scrollHeight
        : 0
      const previousScrollTop = scrollElement
        ? scrollElement.scrollTop
        : 0

      this.isLoadingOlderMessages = true

      try {
        const requestedOffset = this.olderMessagesOffset

        const response = await tgChatApi.messages.list.execute({
          chatId: this.chat.id,
          limit: this.messagesLimit,
          offset: requestedOffset,
        })

        const rawItems = this.extractListItems(response)
        const pagination = this.extractPagination(response)

        if (!rawItems.length) {
          this.hasMoreOlderMessages = false
          return
        }

        const normalizedItems = rawItems.slice().reverse()
        const knownIds = new Set(this.messageList.map(message => message.id))
        const olderItems = normalizedItems.filter(
          message => !knownIds.has(message.id)
        )

        this.messageList = this.mergeMessages(this.messageList, olderItems)

        this.updateOlderMessagesPagination(
          rawItems,
          pagination,
          requestedOffset
        )

        this.$nextTick(() => {
          const nextScrollElement = this.getMessagesScrollElement()

          if (!nextScrollElement) {
            return
          }

          const nextScrollHeight = nextScrollElement.scrollHeight

          nextScrollElement.scrollTop =
            nextScrollHeight - previousScrollHeight + previousScrollTop
        })
      } catch (error) {
      } finally {
        this.isLoadingOlderMessages = false
      }
    },

    handleMessagesScroll() {
      const element = this.getMessagesScrollElement()

      if (!element) {
        return
      }

      const threshold = 80

      if (element.scrollTop <= threshold) {
        this.loadOlderMessages()
      }
    },

    // refresh обновляет сообщения текущего чата
    async manualRefresh() {
      if (!(this.chat && this.chat.id)) {
        this.$emit('refresh-list')
        return
      }

      await this.receiveMessages(this.chat.id, {
        resetPagination: true,
      })

      this.$emit('refresh-list')
    },

    normalizeOutgoingPlaceholder(outgoing, chatId) {
      return {
        id: `outgoing-${outgoing.id}`,
        outgoingId: outgoing.id,
        chatId,
        direction: 'outgoing',
        text: outgoing.text || '',
        error: outgoing.error || '',
        createdAt: outgoing.createdAt || null,
        sentAt: outgoing.sentAt || null,
        sortAt: outgoing.sentAt || outgoing.createdAt || null,
        outgoingStatus: outgoing.status || 'pending',
        isOutgoingPlaceholder: true,
      }
    },

    upsertPendingOutgoing(outgoing, chatId) {
      const normalized = this.normalizeOutgoingPlaceholder(outgoing, chatId)

      const existingIndex = this.pendingOutgoingMessages.findIndex(
        message => message.outgoingId === normalized.outgoingId
      )

      if (existingIndex === -1) {
        this.pendingOutgoingMessages = [
          ...this.pendingOutgoingMessages,
          normalized,
        ]
        return normalized
      }

      const nextItems = [...this.pendingOutgoingMessages]
      nextItems.splice(existingIndex, 1, normalized)
      this.pendingOutgoingMessages = nextItems

      return normalized
    },

    removePendingOutgoing(outgoingId) {
      this.pendingOutgoingMessages = this.pendingOutgoingMessages.filter(
        message => message.outgoingId !== outgoingId
      )
    },

    startOutgoingStatusPolling(outgoingId, chatId) {
      this.stopOutgoingStatusPolling(outgoingId)

      this.outgoingStatusTimers[outgoingId] = setInterval(async () => {
        try {
          const response = await tgChatApi.outgoing.retrieve.execute({
            id: outgoingId,
          })

          const outgoing = this.extractSingleItem(response)

          if (!outgoing) {
            return
          }

          this.upsertPendingOutgoing(outgoing, chatId)

          if (outgoing.status === 'sent') {
            this.stopOutgoingStatusPolling(outgoingId)
            this.removePendingOutgoing(outgoingId)

            if (this.chat && this.chat.id === chatId) {
              await this.receiveMessages(chatId)
            }

            return
          }

          if (outgoing.status === 'failed') {
            this.stopOutgoingStatusPolling(outgoingId)
          }
        } catch (error) {
          this.stopOutgoingStatusPolling(outgoingId)
        }
      }, this.outgoingPollIntervalMs)
    },

    stopOutgoingStatusPolling(outgoingId) {
      const timer = this.outgoingStatusTimers[outgoingId]

      if (timer) {
        clearInterval(timer)
        delete this.outgoingStatusTimers[outgoingId]
      }
    },

    stopAllOutgoingStatusPolling() {
      Object.keys(this.outgoingStatusTimers).forEach(outgoingId => {
        this.stopOutgoingStatusPolling(outgoingId)
      })
    },

    async submitMessage() {
      const text = (this.messageForm.text || '').trim()

      if (!this.canSendMessage || !text || this.isSendingMessage || !(this.chat && this.chat.id)) {
        return
      }

      this.isSendingMessage = true

      try {
        const response = await tgChatApi.messages.send.execute(
          { chatId: this.chat.id },
          { text }
        )

        const outgoing = this.extractSingleItem(response)

        this.messageForm.text = ''

        if (outgoing && outgoing.id) {
          this.upsertPendingOutgoing(outgoing, this.chat.id)
          this.startOutgoingStatusPolling(outgoing.id, this.chat.id)
        }

        this.$toast.success(this._('Message queued for sending'))

        this.$nextTick(() => {
          this.scrollMessagesToBottom()
        })
      } catch (error) {
        this.$toast.error(await this.getSendMessageError(error))
      } finally {
        this.isSendingMessage = false
      }
    },

    async getSendMessageError(error) {
      let payload = null

      try {
        if (error?.response?.data) {
          payload = error.response.data
        } else if (error?.data) {
          payload = error.data
        } else if (typeof error?.json === 'function') {
          payload = await error.json()
        } else {
          payload = error
        }
      } catch (parseError) {
        payload = error
      }

      if (typeof payload === 'string') {
        return payload
      }

      const firstError = Array.isArray(payload?.errors)
        ? payload.errors[0]
        : null

      const state = firstError && firstError.state

      if (state && typeof state === 'object') {
        const fieldNames = [
          'nonFieldErrors',
          'text',
          'chat',
        ]

        for (const fieldName of fieldNames) {
          const fieldErrors = state[fieldName]

          if (
            Array.isArray(fieldErrors)
            && fieldErrors.length
            && fieldErrors[0].message
          ) {
            return fieldErrors[0].message
          }
        }
      }

      if (Array.isArray(payload?.nonFieldErrors) && payload.nonFieldErrors[0]) {
        return payload.nonFieldErrors[0]
      }

      if (payload?.detail) {
        return payload.detail
      }

      if (typeof firstError?.message === 'string' && firstError.message) {
        return firstError.message
      }

      if (typeof payload?.message === 'string' && payload.message) {
        return payload.message
      }

      return this._('Failed to send message')
    },

    restartMessagesPolling() {
      this.stopMessagesPolling()

      if (!(this.chat && this.chat.id)) {
        return
      }

      this.messagesPollTimer = setInterval(async () => {
        const lastMessage = this.getLastRegularMessage()

        const sinceId = lastMessage && lastMessage.id
          ? lastMessage.id
          : null

        if (!sinceId) {
          await this.receiveMessages(this.chat.id)
          return
        }

        await this.receiveMessages(
          this.chat.id,
          {
            sinceId,
            append: true,
          }
        )
      }, this.messagesPollIntervalMs)
    },

    stopMessagesPolling() {
      if (this.messagesPollTimer) {
        clearInterval(this.messagesPollTimer)
        this.messagesPollTimer = null
      }
    },

    stopPolling() {
      this.stopMessagesPolling()
    },

    getMessagesScrollElement() {
      const ref = this.$refs.messagesScroll

      if (Array.isArray(ref)) {
        return ref[0] || null
      }

      return ref || null
    },

    isMessagesNearBottom() {
      const element = this.getMessagesScrollElement()

      if (!element) {
        return true
      }

      const threshold = 80
      const rest = element.scrollHeight - element.scrollTop - element.clientHeight

      return rest <= threshold
    },

    scrollMessagesToBottom() {
      const element = this.getMessagesScrollElement()

      if (!element) {
        return
      }

      element.scrollTop = element.scrollHeight
    },

    getLastRegularMessage() {
      if (!this.messageList.length) {
        return null
      }

      return this.messageList[this.messageList.length - 1]
    },

    getMessageKey(message) {
      if (message.isOutgoingPlaceholder && message.outgoingId) {
        return `outgoing-${message.outgoingId}`
      }

      return message.id
    },

    getOutgoingStatusLabel(status) {
      if (status === 'pending') {
        return this._('Pending')
      }

      if (status === 'sending') {
        return this._('Sending')
      }

      if (status === 'failed') {
        return this._('Failed')
      }

      if (status === 'sent') {
        return this._('Sent')
      }

      return status || ''
    },

    getMessageClasses(message) {
      return [
        `tg-chat-message--${message.direction}`,
        {
          'is-pending':
            message.isOutgoingPlaceholder
            && message.outgoingStatus === 'pending',

          'is-sending':
            message.isOutgoingPlaceholder
            && message.outgoingStatus === 'sending',

          'is-failed':
            message.isOutgoingPlaceholder
            && message.outgoingStatus === 'failed',
        },
      ]
    },

    getDatetime(value) {
      return value ? this.$d(new Date(value), 'datetime') : ''
    },

    getMessageMeta(message) {
      if (message.isOutgoingPlaceholder) {
        const time = this.getDatetime(message.sentAt || message.createdAt)
        const status = this.getOutgoingStatusLabel(message.outgoingStatus)

        return [status, time].filter(Boolean).join(' • ')
      }

      const time = this.getDatetime(message?.sentAt)
      const direction = message?.direction === 'outgoing'
        ? this._('Outgoing')
        : this._('Incoming')

      return [direction, time].filter(Boolean).join(' • ')
    },

    isOutgoingRegularMessage(message) {
      return !!(
        message
        && message.direction === 'outgoing'
        && !message.isOutgoingPlaceholder
      )
    },

    getMessageReadStatus(message) {
      if (!this.isOutgoingRegularMessage(message)) {
        return ''
      }

      if (message.readAt) {
        return `${this._('Read')}: ${this.getDatetime(message.readAt)}`
      }

      return this._('Not read')
    },

    emitSeenIfNeeded() {
      if (!(this.chat && this.chat.id)) {
        return
      }

      if (!Number(this.unreadCount)) {
        return
      }

      if (this.seenEmittedChatId === this.chat.id) {
        return
      }

      this.seenEmittedChatId = this.chat.id
      this.$emit('seen')
    },
  },
}
</script>
